import { useCallback, useEffect, useRef, useState } from 'react';
import {
    CpmButton,
    CpmCheckbox,
    CpmChip,
    CpmFilterDropdown,
    CpmStatusChip,
} from '../../../components/design-system';
import {
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    listStates,
    listTemplateTags,
    listTemplates,
    updateCatalogTemplates,
} from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type { FormPhase, PlaceLoadState, TemplateListRow } from '../../../types/reportsAdmin.types';
import { REPORTS_COPY } from '../copy';
import { stateName } from '../helpers';
import {
    ReportsAlert,
    ReportsDialog,
    ReportsDialogBody,
    ReportsDialogFooter,
    ReportsDialogHeader,
    ReportsSkeletonList,
    ReportsWait,
} from '../components/ReportsDialog';
import AppliedFilterChips, { type AppliedChip } from '../components/AppliedFilterChips';
import FilterToggleChip from '../components/FilterToggleChip';
import FilteredEmpty from '../components/FilteredEmpty';
import InfiniteScrollSentinel from '../components/InfiniteScrollSentinel';
import SearchField from '../components/SearchField';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

interface TemplatesForReportModalProps {
    reportID: string;
    onClose: () => void;
    onSaved: (packsIn: number) => void;
}

export default function TemplatesForReportModal({
    reportID,
    onClose,
    onSaved,
}: TemplatesForReportModalProps) {
    const [search, setSearch] = useState('');
    const debounced = useDebouncedValue(search, 300);
    const [alreadyIn, setAlreadyIn] = useState(true);
    const [states, setStates] = useState<string[]>([]);
    const [tags, setTags] = useState<string[]>([]);
    const [stateOptions, setStateOptions] = useState<CpmState[]>([]);
    const [tagOptions, setTagOptions] = useState<string[]>([]);
    const [tagsError, setTagsError] = useState(false);
    const [rows, setRows] = useState<TemplateListRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [place, setPlace] = useState<PlaceLoadState>('loading');
    const [listBusy, setListBusy] = useState(false);
    const readyOnce = useRef(false);
    const [initialIn, setInitialIn] = useState<Set<string>>(new Set());
    const [ticked, setTicked] = useState<Set<string>>(new Set());
    const [phase, setPhase] = useState<FormPhase>('idle');
    const [error, setError] = useState('');
    const [receipt, setReceipt] = useState('');
    const [moreBusy, setMoreBusy] = useState(false);
    const [moreFailed, setMoreFailed] = useState(false);
    const [statesError, setStatesError] = useState(false);
    const [statesLoading, setStatesLoading] = useState(false);
    const [tagsLoading, setTagsLoading] = useState(false);

    const locked = phase === 'saving';

    const loadOptions = useCallback(async () => {
        setStatesLoading(true);
        setStatesError(false);
        try {
            setStateOptions(await listStates());
        } catch {
            setStatesError(true);
        } finally {
            setStatesLoading(false);
        }
        setTagsLoading(true);
        setTagsError(false);
        try {
            setTagOptions(await listTemplateTags());
        } catch {
            setTagsError(true);
        } finally {
            setTagsLoading(false);
        }
    }, []);

    const load = useCallback(
        async (nextPage: number, append: boolean) => {
            if (!append) {
                setMoreFailed(false);
                if (readyOnce.current) setListBusy(true);
                else setPlace('loading');
            } else {
                setMoreBusy(true);
                setMoreFailed(false);
            }
            try {
                const data = await listTemplates({
                    reportID,
                    alreadyIn,
                    search: debounced || undefined,
                    states: states.length ? states : undefined,
                    tags: tags.length ? tags : undefined,
                    page: nextPage,
                    limit: 25,
                });
                setRows((prev) => (append ? [...prev, ...data.templates] : data.templates));
                setTotal(data.total);
                setPage(nextPage);
                if (!append) {
                    const nextInitial = new Set<string>();
                    const nextTicked = new Set<string>();
                    data.templates.forEach((row) => {
                        if (row.alreadyIn) {
                            nextInitial.add(row._id);
                            nextTicked.add(row._id);
                        }
                    });
                    setInitialIn(nextInitial);
                    setTicked(nextTicked);
                } else {
                    setInitialIn((prev) => {
                        const next = new Set(prev);
                        data.templates.forEach((row) => {
                            if (row.alreadyIn) next.add(row._id);
                        });
                        return next;
                    });
                    setTicked((prev) => {
                        const next = new Set(prev);
                        data.templates.forEach((row) => {
                            if (row.alreadyIn) next.add(row._id);
                        });
                        return next;
                    });
                }
                readyOnce.current = true;
                setPlace('ready');
            } catch (err) {
                if (append) setMoreFailed(true);
                else if (!readyOnce.current) {
                    setError(getReportsAdminErrorMessage(err));
                    setPlace('error');
                }
            } finally {
                setListBusy(false);
                setMoreBusy(false);
            }
        },
        [alreadyIn, debounced, reportID, states, tags]
    );

    useEffect(() => {
        void loadOptions();
    }, [loadOptions]);

    useEffect(() => {
        void load(1, false);
    }, [load]);

    const toggle = (id: string) => {
        if (locked) return;
        setReceipt('');
        setTicked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const inTemplateIds = rows
        .filter((row) => ticked.has(row._id) && !initialIn.has(row._id))
        .map((row) => row._id);
    const outTemplateIds = rows
        .filter((row) => !ticked.has(row._id) && initialIn.has(row._id))
        .map((row) => row._id);
    const dirty = inTemplateIds.length + outTemplateIds.length > 0;

    const persist = async () => {
        if (!dirty) return;
        setPhase('saving');
        setError('');
        try {
            const result = await updateCatalogTemplates({
                reportID,
                inTemplateIds,
                outTemplateIds,
            });
            const nextIn = new Set(ticked);
            setInitialIn(nextIn);
            const packsIn = Array.from(nextIn).length;
            setReceipt(
                `${REPORTS_COPY.SAVED_IN_TEMPLATES} ${packsIn} templates. Added ${result.added} · removed ${result.removed}.`
            );
            setPhase('done');
            onSaved(packsIn);
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const chips: AppliedChip[] = [
        ...states.map((state) => ({
            key: `st-${state}`,
            label: state,
            onClear: () => setStates((prev) => prev.filter((item) => item !== state)),
        })),
        ...tags.map((tag) => ({
            key: `tg-${tag}`,
            label: tag,
            onClear: () => setTags((prev) => prev.filter((item) => item !== tag)),
        })),
    ];

    const hasFilters = Boolean(debounced) || states.length > 0 || tags.length > 0 || alreadyIn;
    const filteredEmpty = place === 'ready' && total === 0 && hasFilters;

    return (
        <ReportsDialog
            open
            width={740}
            zIndex={400}
            onClose={locked ? () => undefined : onClose}
            closeOnBackdrop={!locked}
            closeOnEscape={!locked}
        >
            <ReportsDialogHeader
                kicker={REPORTS_COPY.TEMPLATES}
                title="Which templates include this report"
                subtitle="Tick a template to put this report in it, untick to take it out. Templates that already hold it open ticked. This turns the report on for no store brand."
                onClose={locked ? undefined : onClose}
            />
            <ReportsDialogBody pad="picker">
                {place === 'loading' ? (
                    <div>
                        <div style={{ paddingBottom: 16 }}>
                            <ReportsWait label="Loading templates, and which of them already hold this report…" />
                        </div>
                        <div className="flex" style={{ gap: 9, paddingBottom: 14 }}>
                            <div className="cpm-sk" style={{ height: 36, width: 300 }} />
                            <div className="cpm-sk" style={{ height: 36, width: 108 }} />
                            <div className="cpm-sk" style={{ height: 36, width: 96 }} />
                        </div>
                        <ReportsSkeletonList />
                    </div>
                ) : null}
                {place === 'error' ? (
                    <ReportsAlert
                        tone="danger"
                        title="Couldn't load the templates"
                        body="The template list did not come back, so this says nothing about how many templates exist or which of them hold this report. Nothing was written, and the Catalogue list behind this dialog was not part of this request."
                        reason={reportsAdminErrorReason(error)}
                        action={
                            <CpmButton
                                variant="outline"
                                size="sm"
                                onClick={() => void load(1, false)}
                            >
                                Retry
                            </CpmButton>
                        }
                    />
                ) : null}
                {place === 'ready' ? (
                    <div className="flex flex-col" style={{ gap: 12 }}>
                        {error ? (
                            <ReportsAlert
                                tone="danger"
                                title="Couldn't save the templates for this report"
                                body="No template changed and every tick you made is still here. Retry when you are ready."
                                reason={reportsAdminErrorReason(
                                    error,
                                    'No template changed and every tick you made is still here.'
                                )}
                                action={
                                    <CpmButton
                                        variant="outline"
                                        size="sm"
                                        onClick={() => void persist()}
                                    >
                                        {REPORTS_COPY.RETRY_THE_SAVE}
                                    </CpmButton>
                                }
                            />
                        ) : null}
                        {receipt ? (
                            <ReportsAlert
                                tone="success"
                                title={REPORTS_COPY.SAVED}
                                body={`${receipt} Store brands are untouched: this writes nothing to any store brand.`}
                            />
                        ) : null}
                        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                            <SearchField
                                value={search}
                                onChange={setSearch}
                                placeholder="Search a template by name or tag"
                                disabled={locked}
                                width={300}
                                size="sm"
                            />
                            <CpmFilterDropdown
                                label="State"
                                triggerVariant="chip"
                                portal
                                disabled={locked}
                                menuMinWidth={286}
                                loading={statesLoading}
                                loadingLabel={REPORTS_COPY.LOADING_STATES}
                                failed={statesError}
                                failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                                failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                                onRetry={() => void loadOptions()}
                                options={stateOptions
                                    .map(stateName)
                                    .filter(Boolean)
                                    .map((name) => ({ value: name, label: name }))}
                                selected={states}
                                onChange={setStates}
                            />
                            <CpmFilterDropdown
                                label="Tag"
                                triggerVariant="chip"
                                portal
                                disabled={locked}
                                menuMinWidth={286}
                                loading={tagsLoading}
                                loadingLabel={REPORTS_COPY.LOADING_TAGS}
                                failed={tagsError}
                                failedTitle={REPORTS_COPY.LOAD_FAIL_TAGS}
                                failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                                onRetry={() => void loadOptions()}
                                options={tagOptions.map((tag) => ({ value: tag, label: tag }))}
                                selected={tags}
                                onChange={setTags}
                            />
                            <FilterToggleChip
                                label={REPORTS_COPY.ALREADY_IN_FILTER}
                                on={alreadyIn}
                                disabled={locked}
                                onClick={() => setAlreadyIn((v) => !v)}
                            />
                        </div>
                        <AppliedFilterChips
                            chips={chips}
                            onClearAll={() => {
                                setStates([]);
                                setTags([]);
                            }}
                        />
                        {listBusy ? (
                            <div>
                                <div style={{ paddingBottom: 12 }}>
                                    <ReportsWait label="Matching the templates to that search and those filters…" />
                                </div>
                                <ReportsSkeletonList rows={3} />
                            </div>
                        ) : filteredEmpty ? (
                            <FilteredEmpty
                                restated={debounced ? `“${debounced}”` : undefined}
                                onClear={() => {
                                    setSearch('');
                                    setStates([]);
                                    setTags([]);
                                    setAlreadyIn(false);
                                }}
                            />
                        ) : (
                            <div
                                className="overflow-auto"
                                style={{
                                    maxHeight: 420,
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                }}
                            >
                                {rows.map((row) => {
                                    const checked = ticked.has(row._id);
                                    return (
                                        <div
                                            key={row._id}
                                            className="flex items-center"
                                            style={{
                                                gap: 12,
                                                padding: '11px 16px',
                                                borderBottom: '1px solid var(--cpm-border-default)',
                                            }}
                                        >
                                            <CpmCheckbox
                                                checked={checked}
                                                disabled={locked}
                                                onChange={() => toggle(row._id)}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div style={{ fontSize: 13.5, fontWeight: 800 }}>
                                                    {row.name}
                                                </div>
                                                <div
                                                    className="flex flex-wrap"
                                                    style={{ gap: 4, marginTop: 4 }}
                                                >
                                                    {row.allStates ? (
                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                color: 'var(--cpm-text-secondary)',
                                                            }}
                                                        >
                                                            {REPORTS_COPY.ALL_STATES}
                                                        </span>
                                                    ) : (
                                                        row.states.map((state) => (
                                                            <CpmChip key={state} variant="tag">
                                                                {state}
                                                            </CpmChip>
                                                        ))
                                                    )}
                                                    {row.tags.length === 0 ? (
                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                                color: 'var(--cpm-text-tertiary)',
                                                            }}
                                                        >
                                                            {REPORTS_COPY.NO_TAGS}
                                                        </span>
                                                    ) : (
                                                        row.tags.map((tag) => (
                                                            <CpmChip key={tag} variant="tag">
                                                                {tag}
                                                            </CpmChip>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 800,
                                                    color: row.alreadyIn
                                                        ? 'var(--cpm-success-text)'
                                                        : 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                {row.alreadyIn
                                                    ? REPORTS_COPY.ALREADY_IN_REPORT
                                                    : 'Not in'}
                                            </span>
                                            <CpmStatusChip
                                                status={row.activeStatus.toUpperCase()}
                                            />
                                        </div>
                                    );
                                })}
                                <InfiniteScrollSentinel
                                    hasMore={rows.length < total}
                                    loading={moreBusy}
                                    error={moreFailed}
                                    disabled={locked}
                                    onLoadMore={() => void load(page + 1, true)}
                                    showEnd={rows.length > 0}
                                />
                            </div>
                        )}
                    </div>
                ) : null}
            </ReportsDialogBody>
            <ReportsDialogFooter
                note="Templates only. Store brands are untouched."
                saving={locked}
                savingLabel="Saving which templates include this report. The write has not finished — do not fire it again."
                onCancel={onClose}
                primaryLabel={REPORTS_COPY.SAVE}
                onPrimary={() => void persist()}
                primaryDisabled={place !== 'ready' || listBusy || !dirty}
            />
        </ReportsDialog>
    );
}
